/**
 * Detail-rail primitives (11F.7) — the building blocks of the Student and
 * Staff detail left rails (`student1.png` / `staff1.png` define which blocks
 * exist; D13 defines how they look).
 *
 * Shared rather than duplicated per feature because both rails are the same
 * shape: a stack of titled blocks, some collapsible, containing label/value
 * pairs, plus a quick-action row and a compact insight grid.
 *
 * `RailSection` uses native `<details>/<summary>` deliberately — it is
 * keyboard-accessible and open/closed by default without any JS state, and
 * degrades correctly if styles fail to load.
 */

import React from 'react';
import styled from 'styled-components';

// ── Block ─────────────────────────────────────────────────────────────────

export const RailCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
`;

export const RailHeading = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

// ── Collapsible section ───────────────────────────────────────────────────

const Details = styled.details`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};

  &[open] {
    padding-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const Summary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  cursor: pointer;
  list-style: none;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
  color: ${({ theme }) => theme.colors.textTertiary};

  &::-webkit-details-marker {
    display: none;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  details[open] & svg {
    transform: rotate(180deg);
  }
  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

export const RailSection: React.FC<{
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  testId?: string;
}> = ({ title, defaultOpen = false, children, testId }) => (
  <Details open={defaultOpen} data-testid={testId}>
    <Summary>
      {title}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </Summary>
    {children}
  </Details>
);

// ── Label / value pairs ───────────────────────────────────────────────────

const FieldGrid = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const FieldRow = styled.div`
  min-width: 0;
`;

const FieldTerm = styled.dt`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const FieldDesc = styled.dd`
  margin: 1px 0 0;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  overflow-wrap: anywhere;
`;

export const RailFields: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FieldGrid>{children}</FieldGrid>
);

/** One label/value pair. Renders an em dash when the value is absent, so a
 *  missing field reads as "we know this is empty" rather than as a bug. */
export const RailField: React.FC<{
  label: string;
  value: React.ReactNode;
  testId?: string;
}> = ({ label, value, testId }) => (
  <FieldRow>
    <FieldTerm>{label}</FieldTerm>
    <FieldDesc data-testid={testId}>
      {value === null || value === undefined || value === '' ? (
        <span style={{ color: '#94a3b8', fontWeight: 400 }}>—</span>
      ) : (
        value
      )}
    </FieldDesc>
  </FieldRow>
);

// ── Insights ──────────────────────────────────────────────────────────────

const InsightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
`;

const InsightCell = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.sm} 4px;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const InsightValue = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
`;

const InsightLabel = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.2;
`;

export const RailInsights: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <InsightGrid>{children}</InsightGrid>
);

export const RailInsight: React.FC<{
  label: string;
  value: React.ReactNode;
  testId?: string;
}> = ({ label, value, testId }) => (
  <InsightCell data-testid={testId}>
    <InsightValue>{value ?? '—'}</InsightValue>
    <InsightLabel>{label}</InsightLabel>
  </InsightCell>
);

// ── Quick actions ─────────────────────────────────────────────────────────

const ActionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ActionLink = styled.a<{ $disabled?: boolean }>`
  flex: 1;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: ${({ theme }) => theme.spacing.sm} 4px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.textTertiary : theme.colors.text};
  font-size: 11px;
  text-decoration: none;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    border-color: ${({ theme }) => theme.colors.borderDark};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  svg {
    width: 16px;
    height: 16px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ICONS: Record<string, React.ReactNode> = {
  call: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  message: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.8 14.17c-.25.69-1.45 1.32-2 1.4-.51.08-1.16.11-1.87-.12-.43-.14-.99-.32-1.7-.63-2.99-1.29-4.94-4.3-5.09-4.5-.15-.2-1.22-1.62-1.22-3.09s.77-2.19 1.04-2.49c.27-.3.59-.37.79-.37h.57c.18 0 .43-.07.67.51.25.6.85 2.07.92 2.22.08.15.13.32.02.52-.1.2-.16.32-.31.5-.15.17-.32.39-.46.52-.15.15-.31.31-.13.61.18.3.79 1.3 1.69 2.11 1.16 1.03 2.13 1.35 2.43 1.5.3.15.48.13.66-.08.18-.2.76-.89.96-1.19.2-.3.4-.25.67-.15.27.1 1.72.81 2.02.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z" />
    </svg>
  ),
};

export interface QuickAction {
  key: 'call' | 'message' | 'whatsapp';
  label: string;
  href?: string;
}

/**
 * Contact shortcuts. A missing phone number renders the action disabled
 * rather than hiding it — an absent affordance reads as a broken page,
 * while a greyed one communicates "we have no number on file".
 */
export const RailQuickActions: React.FC<{ actions: QuickAction[]; testId?: string }> = ({
  actions,
  testId,
}) => (
  <ActionRow data-testid={testId}>
    {actions.map((a) => (
      <ActionLink
        key={a.key}
        href={a.href}
        $disabled={!a.href}
        aria-disabled={!a.href}
        target={a.key === 'whatsapp' ? '_blank' : undefined}
        rel={a.key === 'whatsapp' ? 'noopener noreferrer' : undefined}
        data-testid={testId ? `${testId}-${a.key}` : undefined}
      >
        {ICONS[a.key]}
        {a.label}
      </ActionLink>
    ))}
  </ActionRow>
);
